import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Database, FileCode } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ProjectBasicsSection from './ProjectBasicsSection';
import TechnicalDetailsSection from './TechnicalDetailsSection';
import AdditionalFeaturesSection from './AdditionalFeaturesSection';
import TimingResourcesSection from './TimingResourcesSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета стоимости блокчейн-проекта
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setCostDetails,
  projectTypes,
  blockchainPlatforms,
  projectSizes,
  complexityLevels,
  teamLocations,
  saveProject
}) => {
  
  // Состояние для имени сохраняемого проекта
  const [saveProjectName, setSaveProjectName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем сложность автоматически при изменении типа проекта
      if (name === 'projectType') {
        const selectedProjectType = projectTypes.find(type => type.id === fieldValue);
        if (selectedProjectType) {
          newState.smartContractsComplexity = selectedProjectType.defaultComplexity;
          
          // Установка количества смарт-контрактов в зависимости от типа проекта
          switch (fieldValue) {
            case 'token':
              newState.smartContracts = '1';
              break;
            case 'dapp':
              newState.smartContracts = '3';
              break;
            case 'defi':
              newState.smartContracts = '5';
              break;
            case 'nft':
              newState.smartContracts = '4';
              break;
            case 'dao':
              newState.smartContracts = '6';
              break;
            case 'private':
              newState.smartContracts = '8';
              break;
            case 'exchange':
              newState.smartContracts = '10';
              break;
            default:
              break;
          }
          
          // Включение соответствующих функций в зависимости от типа проекта
          if (fieldValue === 'nft') {
            newState.featuresNFT = true;
            newState.includeAdditionalFeatures = true;
          } else if (fieldValue === 'defi' || fieldValue === 'exchange') {
            newState.featuresMultiSig = true;
            newState.includeAdditionalFeatures = true;
          } else if (fieldValue === 'dao') {
            newState.featuresDAO = true;
            newState.includeAdditionalFeatures = true;
          }
        }
      }

      // Если платформа Ethereum, то умеем включать MultiSig
      if (name === 'blockchainPlatform' && fieldValue === 'ethereum') {
        if (prev.projectType === 'defi' || prev.projectType === 'dao' || prev.projectType === 'exchange') {
          newState.featuresMultiSig = true;
          newState.includeAdditionalFeatures = true;
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeAdditionalFeatures' && !checked) {
        newState.featuresKYC = false;
        newState.featuresMultiSig = false;
        newState.featuresDAO = false;
        newState.featuresNFT = false;
        newState.featuresDEX = false;
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [projectTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      projectType, projectSize, blockchainPlatform, 
      smartContracts, projectDuration, maintenanceMonths,
      teamLocation, smartContractsComplexity, frontendComplexity
    } = formData;

    // Парсинг числовых значений
    const numSmartContracts = parseIntOrDefault(smartContracts, -1);
    const numProjectDuration = parseFloatOrDefault(projectDuration, -1);
    const numMaintenanceMonths = parseIntOrDefault(maintenanceMonths, -1);

    // Валидация базовых полей
    if (!projectType) newErrors.projectType = 'Выберите тип проекта.';
    if (!projectSize) newErrors.projectSize = 'Выберите размер проекта.';
    if (!blockchainPlatform) newErrors.blockchainPlatform = 'Выберите блокчейн-платформу.';
    
    if (numSmartContracts < 0) newErrors.smartContracts = 'Введите корректное число смарт-контрактов.';
    if (numSmartContracts > 50) newErrors.smartContracts = 'Максимальное число смарт-контрактов: 50.';
    
    if (!smartContractsComplexity) newErrors.smartContractsComplexity = 'Выберите сложность смарт-контрактов.';
    if (!frontendComplexity) newErrors.frontendComplexity = 'Выберите сложность пользовательского интерфейса.';
    
    if (numProjectDuration <= 0) newErrors.projectDuration = 'Срок должен быть положительным.';
    if (numProjectDuration > 36) newErrors.projectDuration = 'Максимальный срок 36 месяцев.';
    
    if (numMaintenanceMonths < 0) newErrors.maintenanceMonths = 'Месяцы обслуживания не могут быть отрицательными.';
    if (numMaintenanceMonths > 60) newErrors.maintenanceMonths = 'Максимальный срок обслуживания 60 месяцев.';
    
    if (!teamLocation) newErrors.teamLocation = 'Выберите место расположения команды.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета стоимости проекта
  const calculateProjectCost = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setCostDetails([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          projectType, projectSize, blockchainPlatform, customTokenRequired,
          smartContracts, smartContractsComplexity, frontendComplexity,
          backendIntegration, securityAudit, testingLevel,
          deploymentSupport, maintenanceMonths, teamLocation,
          timeConstraint, projectDuration,
          includeAdditionalFeatures, featuresKYC, featuresMultiSig, featuresDAO, featuresNFT, featuresDEX,
          customUIRequirements
        } = formData;

        // --- Расчет базовой стоимости ---
        // Базовая стоимость зависит от размера проекта
        const selectedProjectSize = projectSizes.find(size => size.id === projectSize) || projectSizes[1]; // Medium по умолчанию
        const baseProjectCost = (() => {
          switch (projectSize) {
            case 'small': return 15000;
            case 'medium': return 45000;
            case 'large': return 100000;
            case 'enterprise': return 250000;
            default: return 45000;
          }
        })();

        // Коэффициент сложности смарт-контрактов
        const selectedContractComplexity = complexityLevels.find(level => level.id === smartContractsComplexity) || complexityLevels[1];
        const contractComplexityFactor = selectedContractComplexity.costFactor;

        // Коэффициент сложности UI
        const selectedFrontendComplexity = complexityLevels.find(level => level.id === frontendComplexity) || complexityLevels[1];
        const frontendComplexityFactor = selectedFrontendComplexity.costFactor;

        // Коэффициент стоимости блокчейн-платформы
        const selectedPlatform = blockchainPlatforms.find(p => p.id === blockchainPlatform) || blockchainPlatforms[0];
        const platformCostFactor = selectedPlatform.costFactor;

        // Коэффициент стоимости в зависимости от расположения команды
        const selectedTeamLocation = teamLocations.find(loc => loc.id === teamLocation) || teamLocations[0];
        const teamLocationFactor = selectedTeamLocation.costFactor;

        // Коэффициент сложности в зависимости от временных ограничений
        const timeConstraintFactor = (() => {
          switch (timeConstraint) {
            case 'urgent': return 1.5;
            case 'fast': return 1.25;
            case 'normal': return 1.0;
            case 'relaxed': return 0.9;
            default: return 1.0;
          }
        })();

        // --- Расчет стоимости по компонентам ---
        // Стоимость разработки смарт-контрактов ($/контракт)
        const smartContractBaseCost = (() => {
          switch (smartContractsComplexity) {
            case 'low': return 3000;
            case 'medium': return 7000;
            case 'high': return 15000;
            case 'very_high': return 25000;
            default: return 7000;
          }
        })();
        const numSmartContracts = parseInt(smartContracts) || 0;
        const smartContractsCost = numSmartContracts * smartContractBaseCost * platformCostFactor;

        // Стоимость разработки токена (если требуется)
        const tokenDevelopmentCost = customTokenRequired ? 10000 * platformCostFactor : 0;

        // Стоимость фронтенда
        const frontendBaseCost = (() => {
          switch (frontendComplexity) {
            case 'low': return 8000;
            case 'medium': return 20000;
            case 'high': return 40000;
            case 'very_high': return 80000;
            default: return 20000;
          }
        })();
        const frontendCost = frontendBaseCost * selectedProjectSize.costFactor;

        // Стоимость бэкенд-интеграции
        const backendIntegrationCost = backendIntegration ? 15000 * selectedProjectSize.costFactor : 0;

        // Стоимость аудита безопасности
        const securityAuditCost = securityAudit ? 
          Math.max(5000, numSmartContracts * 2000 * contractComplexityFactor) : 0;

        // Стоимость тестирования
        const testingBaseCost = (() => {
          switch (testingLevel) {
            case 'basic': return baseProjectCost * 0.1;
            case 'standard': return baseProjectCost * 0.15;
            case 'comprehensive': return baseProjectCost * 0.25;
            case 'extensive': return baseProjectCost * 0.35;
            default: return baseProjectCost * 0.15;
          }
        })();
        const testingCost = testingBaseCost * contractComplexityFactor;

        // Стоимость развертывания и поддержки
        const deploymentCost = deploymentSupport ? 5000 * selectedProjectSize.costFactor : 0;
        
        // Стоимость обслуживания (за месяц)
        const maintenanceMonthlyRate = baseProjectCost * 0.05;
        const numMaintenanceMonths = parseInt(maintenanceMonths) || 0;
        const maintenanceCost = numMaintenanceMonths * maintenanceMonthlyRate;

        // Стоимость дополнительных функций
        let additionalFeaturesCost = 0;
        const additionalFeaturesItems = [];

        if (includeAdditionalFeatures) {
          if (featuresKYC) {
            const kycCost = 15000 * selectedProjectSize.costFactor;
            additionalFeaturesCost += kycCost;
            additionalFeaturesItems.push({ 
              name: 'KYC/AML интеграция', 
              amount: kycCost 
            });
          }
          
          if (featuresMultiSig) {
            const multiSigCost = 8000 * contractComplexityFactor;
            additionalFeaturesCost += multiSigCost;
            additionalFeaturesItems.push({ 
              name: 'Мультиподпись (MultiSig)', 
              amount: multiSigCost 
            });
          }
          
          if (featuresDAO) {
            const daoCost = 25000 * contractComplexityFactor;
            additionalFeaturesCost += daoCost;
            additionalFeaturesItems.push({ 
              name: 'Структура DAO', 
              amount: daoCost 
            });
          }
          
          if (featuresNFT) {
            const nftCost = 20000 * platformCostFactor;
            additionalFeaturesCost += nftCost;
            additionalFeaturesItems.push({ 
              name: 'Функциональность NFT', 
              amount: nftCost 
            });
          }
          
          if (featuresDEX) {
            const dexCost = 35000 * contractComplexityFactor * platformCostFactor;
            additionalFeaturesCost += dexCost;
            additionalFeaturesItems.push({ 
              name: 'Функционал DEX', 
              amount: dexCost 
            });
          }
        }

        // Стоимость особых требований к UI
        const customUICost = (() => {
          switch (customUIRequirements) {
            case 'minimal': return frontendCost * 0.7;
            case 'standard': return frontendCost;
            case 'premium': return frontendCost * 1.5;
            case 'custom': return frontendCost * 2.0;
            default: return frontendCost;
          }
        })() - frontendCost;

        // --- Корректировка стоимости в зависимости от расположения команды и временных ограничений ---
        // Базовая разработка, не включая внешние услуги (аудит безопасности и инфраструктуру)
        const baseDevelopmentCost = smartContractsCost + tokenDevelopmentCost + frontendCost + backendIntegrationCost + testingCost + customUICost;
        const teamLocationAdjustedCost = baseDevelopmentCost * teamLocationFactor;
        const timeConstraintAdjustedCost = teamLocationAdjustedCost * timeConstraintFactor;
        
        // Разница в стоимости из-за расположения команды
        const teamLocationAdjustment = teamLocationAdjustedCost - baseDevelopmentCost;
        // Разница в стоимости из-за временных ограничений
        const timeConstraintAdjustment = timeConstraintAdjustedCost - teamLocationAdjustedCost;

        // --- Итоговая стоимость ---
        const totalDevelopmentCost = timeConstraintAdjustedCost + securityAuditCost + deploymentCost + additionalFeaturesCost;
        const totalProjectCost = totalDevelopmentCost + maintenanceCost;

        // --- Вычисляем сроки и команду ---
        // Базовая длительность проекта на основе входных данных (в месяцах)
        const projDuration = parseFloat(projectDuration) || 4;
        
        // Размер команды зависит от сложности, размера и временных ограничений
        const baseTeamSize = (() => {
          switch (projectSize) {
            case 'small': return 2;
            case 'medium': return 4;
            case 'large': return 8;
            case 'enterprise': return 15;
            default: return 4;
          }
        })();
        
        // Корректировка размера команды с учетом временных ограничений
        const teamSizeAdjustment = (() => {
          switch (timeConstraint) {
            case 'urgent': return 1.7;
            case 'fast': return 1.3;
            case 'normal': return 1.0;
            case 'relaxed': return 0.8;
            default: return 1.0;
          }
        })();
        
        // Определение состава команды
        const teamSize = Math.round(baseTeamSize * teamSizeAdjustment);
        
        // --- Формируем детальную разбивку стоимости для отображения ---
        const detailedCostBreakdown = [
          { category: 'Смарт-контракты', name: `Разработка ${numSmartContracts} смарт-контрактов (${selectedContractComplexity.name} сложность)`, amount: smartContractsCost },
          customTokenRequired && { category: 'Смарт-контракты', name: 'Разработка собственного токена', amount: tokenDevelopmentCost },
          { category: 'Frontend', name: `Разработка пользовательского интерфейса (${selectedFrontendComplexity.name} сложность)`, amount: frontendCost },
          customUICost !== 0 && { category: 'Frontend', name: `Доп. работы по UI (${customUIRequirements})`, amount: customUICost },
          backendIntegration && { category: 'Backend', name: 'Интеграция с бэкендом и API', amount: backendIntegrationCost },
          securityAudit && { category: 'Безопасность', name: 'Аудит безопасности смарт-контрактов', amount: securityAuditCost },
          { category: 'Тестирование', name: `${testingLevel === 'comprehensive' ? 'Комплексное' : testingLevel === 'extensive' ? 'Расширенное' : testingLevel === 'standard' ? 'Стандартное' : 'Базовое'} тестирование`, amount: testingCost },
          deploymentSupport && { category: 'Деплоймент', name: 'Развертывание и настройка', amount: deploymentCost },
          teamLocationAdjustment !== 0 && { category: 'Локация', name: `Корректировка стоимости (${selectedTeamLocation.name})`, amount: teamLocationAdjustment },
          timeConstraintAdjustment !== 0 && { category: 'Сроки', name: `Корректировка по срокам (${timeConstraint === 'urgent' ? 'Срочно' : timeConstraint === 'fast' ? 'Быстро' : timeConstraint === 'normal' ? 'Обычно' : 'Без спешки'})`, amount: timeConstraintAdjustment },
          ...additionalFeaturesItems.map(feature => ({ 
            category: 'Доп. функции', 
            name: feature.name, 
            amount: feature.amount 
          })),
          numMaintenanceMonths > 0 && { category: 'Поддержка', name: `Техническая поддержка (${numMaintenanceMonths} мес.)`, amount: maintenanceCost }
        ].filter(Boolean);

        // Расчет ежемесячных затрат на разработку
        const monthlyDevelopmentCost = totalDevelopmentCost / projDuration;
        
        // Общие человеко-месяцы проекта
        const totalManMonths = teamSize * projDuration;
        
        // Средняя ставка специалиста в месяц
        const avgMonthlyRatePerDeveloper = totalDevelopmentCost / totalManMonths;

        // --- Подготовка результатов ---
        const finalResults = {
          // Основная информация
          projectType: projectType,
          blockchainPlatform: blockchainPlatform,
          projectSize: projectSize,
          smartContractsCount: numSmartContracts,
          
          // Стоимость разработки
          smartContractsCost: smartContractsCost,
          tokenDevelopmentCost: tokenDevelopmentCost,
          frontendCost: frontendCost + customUICost,
          backendCost: backendIntegrationCost,
          testingCost: testingCost,
          securityAuditCost: securityAuditCost,
          deploymentCost: deploymentCost,
          additionalFeaturesCost: additionalFeaturesCost,
          maintenanceCost: maintenanceCost,
          
          // Итоговые суммы
          developmentCost: totalDevelopmentCost,
          totalCost: totalProjectCost,
          monthlyDevelopmentCost: monthlyDevelopmentCost,
          
          // Сроки и ресурсы
          duration: projDuration,
          teamSize: teamSize,
          totalManMonths: totalManMonths,
          avgMonthlyRatePerDeveloper: avgMonthlyRatePerDeveloper,
          
          // Детальная разбивка
          costBreakdown: detailedCostBreakdown,
          
          // Информация о технологиях
          language: selectedPlatform.language,
          
          // Дополнительная информация для отображения
          platformName: selectedPlatform.name,
          projectTypeName: projectTypes.find(t => t.id === projectType)?.name || projectType,
          teamLocation: selectedTeamLocation.name,
          timeConstraint: timeConstraint,
          maintenanceMonths: numMaintenanceMonths
        };

        setResults(finalResults);
        setCostDetails(detailedCostBreakdown);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setCostDetails([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setCostDetails, setIsCalculating, projectSizes, complexityLevels, blockchainPlatforms, teamLocations, projectTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      projectType: 'dapp',
      projectSize: 'medium',
      blockchainPlatform: 'ethereum',
      customTokenRequired: false,
      smartContracts: '3',
      smartContractsComplexity: 'medium',
      frontendComplexity: 'medium',
      backendIntegration: true,
      securityAudit: true,
      testingLevel: 'comprehensive',
      deploymentSupport: true,
      maintenanceMonths: '6',
      teamLocation: 'mixed',
      timeConstraint: 'normal',
      projectDuration: '4',
      includeAdditionalFeatures: false,
      featuresKYC: false,
      featuresMultiSig: false,
      featuresDAO: false,
      featuresNFT: false,
      featuresDEX: false,
      customUIRequirements: 'standard',
    });
    setResults(null);
    setCostDetails([]);
    setErrors({});
    setSaveProjectName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов расчета в CSV
  const exportToCSV = () => {
    if (!results || !results.costBreakdown || results.costBreakdown.length === 0) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки и данные для основной информации
    const infoHeaders = ['Параметр', 'Значение'];
    const infoRows = [
      ['Тип проекта', results.projectTypeName],
      ['Блокчейн-платформа', results.platformName],
      ['Язык программирования', results.language],
      ['Размер проекта', projectSizes.find(s => s.id === results.projectSize)?.name || results.projectSize],
      ['Количество смарт-контрактов', results.smartContractsCount],
      ['Расположение команды', results.teamLocation],
      ['Срок разработки', `${results.duration} мес.`],
      ['Размер команды', `${results.teamSize} человек`],
      ['Всего человеко-месяцев', results.totalManMonths],
      ['Стоимость разработки', `${Math.round(results.developmentCost).toLocaleString()}`],
      ['Стоимость обслуживания', `${Math.round(results.maintenanceCost).toLocaleString()}`],
      ['Общая стоимость', `${Math.round(results.totalCost).toLocaleString()}`],
      [''],  // Пустая строка для разделения
    ];

    // Определяем заголовки и данные для детальной разбивки затрат
    const costHeaders = ['Категория', 'Статья расходов', 'Сумма ($)'];
    const costRows = results.costBreakdown.map(item => [
      item.category,
      item.name,
      Math.round(item.amount).toLocaleString()
    ]);

    // Добавим итоговую строку
    costRows.push(['', 'ИТОГО:', Math.round(results.totalCost).toLocaleString()]);

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [
      ...infoRows.map(row => row.join(';')),
      '',
      costHeaders.join(';'),
      ...costRows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_стоимости_блокчейн_проекта.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения проекта
  const handleSaveProject = () => {
    if (showSaveDialog) {
      const savedResult = saveProject(saveProjectName || null);
      if (savedResult) {
        alert(`Проект "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProjectName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateProjectCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <FileCode className="w-6 h-6 text-indigo-500" />
          Параметры проекта
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры проекта */}
        <ProjectBasicsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          projectTypes={projectTypes}
          blockchainPlatforms={blockchainPlatforms}
          projectSizes={projectSizes}
        />

        {/* Технические детали */}
        <TechnicalDetailsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          complexityLevels={complexityLevels}
        />

        {/* Дополнительные функции */}
        <AdditionalFeaturesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Сроки и ресурсы */}
        <TimingResourcesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          teamLocations={teamLocations}
        />

        {/* Диалог сохранения проекта */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProjectName}
                onChange={(e) => setSaveProjectName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProject}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Экспорт расчета (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProject}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;